import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";

import { organizationSchema, websiteSchema } from "@/lib/org-schema";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { lazy, Suspense } from "react";
import { Toaster } from "sonner";
import { ChartInteractivity } from "../components/site/ChartInteractivity";
import { makeSeoDescription } from "@/lib/seo-description";
import { AnalyticsTracker } from "../components/site/AnalyticsTracker";

const CommunityStories = lazy(() => import("../components/site/CommunityStories").then(m => ({ default: m.CommunityStories })));
const CookieConsent = lazy(() => import("../components/site/CookieConsent").then(m => ({ default: m.CookieConsent })));

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link title="Return to the LifeWestWay homepage"
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground transition-colors hover:bg-navy/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error: rawError, reset }: { error: unknown; reset: () => void }) {
  const error = rawError instanceof Error ? rawError : new Error(String(rawError));
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground transition-colors hover:bg-navy/90"
          >
            Try again
          </button>
          <a title="Return to the LifeWestWay homepage"
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Western Migration & Jobs Guide | LifeWestWay" },
      {
        name: "description",
        content:
          makeSeoDescription("Step-by-step immigration, verified part-time jobs, tax, and personal finance strategies for USA, UK, Australia, Italy, Portugal, and Canada."),
      },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "author", content: "LifeWestWay Editorial" },
      { name: "theme-color", content: "#0A192F" },
      { name: "google-adsense-account", content: "ca-pub-1377262840772102" },
      { property: "og:site_name", content: "LifeWestWay" },
      { property: "og:title", content: "Western Migration & Jobs Guide | LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("Immigration pathways, verified part-time jobs, tax and finance guides for six top Western destinations."),
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "language", content: "English" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@LifeWestWay" },
      { name: "twitter:title", content: "Western Migration & Jobs Guide | LifeWestWay" },
      { name: "twitter:description", content: makeSeoDescription("Immigration pathways, verified part-time jobs, tax and finance guides for six top Western destinations.") },
      { name: "application-name", content: "LifeWestWay" },
      { name: "apple-mobile-web-app-title", content: "LifeWestWay" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      {
        rel: "preconnect",
        href: "https://flagcdn.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "style",
        href: "/__l5e/critical.css",
      },

      // Fonts are self-hosted (see @font-face in styles.css) — no third-party
      // font origins on the critical path. Preload the two above-the-fold
      // faces so headings/body text paint with the final font immediately.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/95bd6f85-4f40-42db-86d8-1159e6922cda/playfair-latin.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/__l5e/assets-v1/56bcc8d4-db25-47d9-a1e8-eadda4b83cc9/inter-latin.woff2",
        crossOrigin: "anonymous",
      },
      // Preload the cinematic hero image for desktop to improve LCP.
      // Mobile preload is handled via route-specific links in index.tsx.
      {
        rel: "preload",
        as: "image",
        href: "/__l5e/assets-v1/c2250917-0248-4350-934c-4a378d3864a7/hero-cinematic.jpg",
        media: "(min-width: 768px)",
        fetchPriority: "high",
      },
      { rel: "preconnect", href: "https://upload.wikimedia.org", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://upload.wikimedia.org" },
      { rel: "preconnect", href: "https://flagcdn.com", crossOrigin: "anonymous" },
    ],
    scripts: [
      {

        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [organizationSchema(), websiteSchema()],
        }),
      },
      {
        defer: true,
        src: "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1377262840772102",
        crossOrigin: "anonymous",
      },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en-US">
      <head>
        <style dangerouslySetInnerHTML={{ __html: `
          :root {
            --background: oklch(0.13 0.01 260);
            --foreground: oklch(0.98 0.005 250);
            --accent: oklch(0.78 0.13 85);
            --navy: oklch(0.18 0.03 260);
            --font-display: "Playfair Display", ui-serif, Georgia, serif;
            --font-sans: "Inter", ui-sans-serif, system-ui, sans-serif;
          }
          body { 
            background-color: #0a0a0c; 
            color: #f8f8f8; 
            margin: 0; 
            font-family: var(--font-sans);
            -webkit-font-smoothing: antialiased;
          }
          .hero-gradient { background: radial-gradient(circle at 20% 30%, rgba(212,175,55,0.15), transparent 70%); }
          header { position: sticky; top: 0; z-index: 50; width: 100%; min-height: 80px; }
          .max-w-7xl { max-width: 80rem; margin-left: auto; margin-right: auto; }
          h1, h2 { font-family: var(--font-display); }
        `}} />
        <HeadContent />
      </head>
      <body>

        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hideCommunity = pathname.startsWith("/admin");

  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          <Outlet />
        </main>

        <Suspense fallback={null}>
          {hideCommunity ? null : <CommunityStories />}
        </Suspense>
        <Footer />
      </div>

      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Toaster richColors position="top-right" />
      <ChartInteractivity />
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
    </QueryClientProvider>
  );
}
